import type * as React from "react"
import { useTranslation } from "react-i18next"
import { Link, Outlet, useLocation } from "react-router-dom"
import {
  NavigationMenu,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
} from "@/components/ui/navigation-menu"
import { useAuth } from "@/features/auth/auth-context"
import { visibleSettingsSections } from "@/features/settings/settings-sections"
import { cn } from "@/lib/utils"

/**
 * The settings area's frame: the section menu, and whichever section is open.
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * `NavigationMenu`, NOT `Tabs` AND NOT `Menubar`. All three primitives are
 * already in `components/ui/`, so this was a choice among things present rather
 * than a dependency.
 *
 * EACH SECTION IS A REAL ROUTE. Tabs render a `tablist`/`tabpanel` relationship,
 * which tells a screen reader the panels live on this page — a lie about five
 * separate URLs, each behind its own capability guard, any one of which can be
 * bookmarked, deep-linked or refused. Menubar is for application command menus
 * (File, Edit, View) and is the wrong idiom for going somewhere. NavigationMenu
 * renders a `<nav>` of `<a href>` links, which is what this is, so the back
 * button, a middle click and a link sent over WhatsApp all keep working BY
 * CONSTRUCTION rather than by somebody remembering to preserve them.
 *
 * THE CURRENT ITEM IS MARKED TWICE, ON PURPOSE. `active` gives it the primitive's
 * own `data-active` styling for the eye; `aria-current="page"` is what a screen
 * reader announces. Neither substitutes for the other — a highlight nobody can
 * hear is as incomplete as an attribute nobody can see.
 *
 * THE FRAME IS NOT GATED. Each section route keeps its own `RequireCapability`
 * in `routes/index.tsx`; this component only decides what to OFFER, from the
 * same capability map (§2.1 — `useAuth().can`, never `user.role === "ADMIN"`).
 * A reader who holds `user.manage` and nothing else sees exactly one item, and
 * the item he cannot open is not rendered rather than rendered disabled.
 * ═════════════════════════════════════════════════════════════════════════════
 */
export function SettingsLayout(): React.JSX.Element {
  const { t } = useTranslation()
  const { can } = useAuth()
  const { pathname } = useLocation()

  const sections = visibleSettingsSections(can)

  /*
   * A MENU OF ONE ITEM IS NOT NAVIGATION — it is the screen's own title printed
   * a second time, above the screen. It is a real case rather than a hypothetical
   * one: a future role holding only `user.manage` has one section, and so does
   * the OWNER on §6.1's counters screen, which renders inside this frame so that
   * following the item from the menu is not a one-way door.
   */
  const showMenu = sections.length > 1

  return (
    <div className="flex w-full flex-col">
      {showMenu ? (
        <div className="border-b px-4 pt-4 md:px-6 md:pt-6">
          {/*
            * The root renders the `<nav>` landmark, so it is named — a page can
            * hold several navs and "Settings" is what this one is. `max-w-none`
            * and `justify-start` undo the primitive's centred, content-width
            * default: this is a bar across the top of the area, not a floating
            * menu in the middle of it.
            */}
          <NavigationMenu
            aria-label={t("nav.settings")}
            className="w-full max-w-none flex-none justify-start"
          >
            <NavigationMenuList className="w-full justify-start gap-1 overflow-x-auto pb-2">
              {sections.map((section) => {
                /*
                 * EXACT, NOT A PREFIX. `/settings` is a prefix of every other
                 * path in here, so a `startsWith` comparison would light Business
                 * details on all five screens — the highlight would say "you are
                 * here" everywhere, which teaches the reader to stop trusting it.
                 */
                const current = pathname === section.url

                return (
                  <NavigationMenuItem key={section.url}>
                    <NavigationMenuLink
                      active={current}
                      aria-current={current ? "page" : undefined}
                      /*
                       * `render` hands the anchor to React Router. A bare
                       * `<a href>` here would reload the whole bundle and throw
                       * away every query cache — navigation that costs a cold
                       * start, which on the workshop connection is seconds.
                       */
                      render={<Link to={section.url} />}
                      className={cn(
                        "whitespace-nowrap px-3 py-1.5",
                        current && "bg-muted font-medium text-foreground",
                      )}
                    >
                      {t(section.titleKey)}
                    </NavigationMenuLink>
                  </NavigationMenuItem>
                )
              })}
            </NavigationMenuList>
          </NavigationMenu>
        </div>
      ) : null}

      <Outlet />
    </div>
  )
}
